import { useEffect, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from './auth';
import { trackPageview } from './analytics';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ResetPage } from './pages/ResetPage';
import { DashboardPage } from './pages/DashboardPage';
import { DevicesPage } from './pages/DevicesPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { PlansPage } from './pages/PlansPage';
import { ReferralPage } from './pages/ReferralPage';
import { SettingsPage } from './pages/SettingsPage';
import { SupportPage } from './pages/SupportPage';

function Protected({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="container">Загрузка…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return <>{children}</>;
}

// Routes that only make sense for unauthenticated visitors (login/register/reset).
// If an authenticated user lands here, send them to the dashboard (or their
// originally requested page). The password-reset page is an exception when it
// carries a recovery token, since that flow can legitimately run while signed in.
function PublicOnly({ children, allowWithResetToken = false }: { children: React.ReactNode; allowWithResetToken?: boolean }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  if (loading) return <div className="container">Загрузка…</div>;
  if (user && !(allowWithResetToken && searchParams.get('token'))) {
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
      <Route path="/reset" element={<PublicOnly allowWithResetToken><ResetPage /></PublicOnly>} />
      <Route path="/" element={<Protected><DashboardPage /></Protected>} />
      <Route path="/plans" element={<Protected><PlansPage /></Protected>} />
      <Route path="/devices" element={<Protected><DevicesPage /></Protected>} />
      <Route path="/payments" element={<Protected><PaymentsPage /></Protected>} />
      <Route path="/referral" element={<Protected><ReferralPage /></Protected>} />
      <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
      <Route path="/support" element={<Protected><SupportPage /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const navItems = [
  { to: '/', label: 'Подписка', end: true },
  { to: '/plans', label: 'Тарифы' },
  { to: '/devices', label: 'Устройства' },
  { to: '/payments', label: 'Платежи' },
  { to: '/referral', label: 'Рефералки' },
  { to: '/settings', label: 'Настройки' },
  { to: '/support', label: 'Поддержка' }
];

export function App() {
  const { user, logout } = useAuth();
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setNavOpen(false);
    window.scrollTo(0, 0);
    trackPageview(location.pathname + location.search);
  }, [location.pathname]);

  if (!user) {
    return <AppRoutes />;
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <button
          className="nav-burger"
          aria-label="Меню"
          aria-expanded={navOpen}
          onClick={() => setNavOpen((v) => !v)}
        >
          <span />
          <span />
          <span />
        </button>
        <a className="brand" href="/">
          <img src="/logo.png" alt="OrbanVPN" width="32" height="32" />
          <span className="brand-name">OrbanVPN</span>
        </a>
        <div className="nav-user">
          <span className="email" title={user.email ?? ''}>{user.email}</span>
          <button className="secondary" onClick={() => void logout()}>Выйти</button>
        </div>
      </header>
      <div className={`app-body${navOpen ? ' nav-open' : ''}`}>
        <aside className="side-nav">
          <nav>
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => (isActive ? 'active' : '')}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <div className="side-nav-backdrop" onClick={() => setNavOpen(false)} />
        <main className="app-main">
          <AppRoutes />
        </main>
      </div>
    </div>
  );
}
